import React from 'react';

interface AIBrainIconProps {
  size?: number;
  color?: string;
  className?: string;
  animate?: boolean;
}

const AIBrainIcon: React.FC<AIBrainIconProps> = ({ 
  size = 24, 
  color = 'currentColor',
  className = '',
  animate = false
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`ai-brain-icon ${animate ? 'animate' : ''} ${className}`}
    >
      {/* Brain outline */}
      <path
        d="M12 2C8.5 2 6 4.5 6 8c0 1.5.5 2.8 1.2 3.9C6.5 12.5 6 13.7 6 15c0 2.8 2.2 5 5 5h2c2.8 0 5-2.2 5-5 0-1.3-.5-2.5-1.2-3.1C17.5 10.8 18 9.5 18 8c0-3.5-2.5-6-6-6z"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        opacity="0.8"
      />
      
      {/* Neural pathways - left hemisphere */}
      <path
        d="M8 8.5c1 0 2 .5 2.5 1.5"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.6"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="2s"
            repeatCount="indefinite"
          />
        )}
      </path>
      
      <path
        d="M8.5 11c.5 0 1 .3 1.5 1"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.5"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.5;0.9;0.5"
            dur="2.3s"
            repeatCount="indefinite"
          />
        )}
      </path>
      
      <path
        d="M9 13.5c.8 0 1.2.5 1.5 1"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.7"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.7;1;0.7"
            dur="1.8s"
            repeatCount="indefinite"
          />
        )}
      </path>

      {/* Neural pathways - right hemisphere */}
      <path
        d="M16 8.5c-1 0-2 .5-2.5 1.5"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.6"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="2.1s"
            repeatCount="indefinite"
          />
        )}
      </path>
      
      <path
        d="M15.5 11c-.5 0-1 .3-1.5 1"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.5"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.5;0.9;0.5"
            dur="1.9s"
            repeatCount="indefinite"
          />
        )}
      </path>
      
      <path
        d="M15 13.5c-.8 0-1.2.5-1.5 1"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.7"
      >
        {animate && (
          <animate
            attributeName="opacity"
            values="0.7;1;0.7"
            dur="2.2s"
            repeatCount="indefinite"
          />
        )}
      </path>

      {/* Central processing core */}
      <circle
        cx="12"
        cy="12"
        r="2.5"
        fill="none"
        stroke={color}
        strokeWidth="0.8"
        opacity="0.4"
      >
        {animate && (
          <animate
            attributeName="r"
            values="2.5;3;2.5"
            dur="3s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Synaptic nodes */}
      <circle cx="9" cy="7" r="1" fill={color} opacity="0.7">
        {animate && (
          <animate
            attributeName="opacity"
            values="0.7;1;0.7"
            dur="1.5s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="15" cy="7" r="1" fill={color} opacity="0.8">
        {animate && (
          <animate
            attributeName="opacity"
            values="0.8;1;0.8"
            dur="1.7s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="8" cy="12" r="0.8" fill={color} opacity="0.6">
        {animate && (
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="2.1s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="16" cy="12" r="0.8" fill={color} opacity="0.6">
        {animate && (
          <animate
            attributeName="opacity"
            values="0.6;1;0.6"
            dur="1.9s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="10" cy="16" r="0.8" fill={color} opacity="0.7">
        {animate && (
          <animate
            attributeName="opacity"
            values="0.7;1;0.7"
            dur="2.3s"
            repeatCount="indefinite"
          />
        )}
      </circle>
      
      <circle cx="14" cy="16" r="0.8" fill={color} opacity="0.7">
        {animate && (
          <animate
            attributeName="opacity"
            values="0.7;1;0.7"
            dur="1.8s"
            repeatCount="indefinite"
          />
        )}
      </circle>

      {/* Data flow indicators */}
      <path
        d="M12 6 L12 10"
        stroke={color}
        strokeWidth="1.5"
        opacity="0.3"
        strokeDasharray="2,2"
      >
        {animate && (
          <animate
            attributeName="stroke-dashoffset"
            values="0;4"
            dur="1s"
            repeatCount="indefinite"
          />
        )}
      </path>
      
      <path
        d="M12 14 L12 18"
        stroke={color}
        strokeWidth="1.5"
        opacity="0.3"
        strokeDasharray="2,2"
      >
        {animate && (
          <animate
            attributeName="stroke-dashoffset"
            values="0;4"
            dur="1s"
            repeatCount="indefinite"
          />
        )}
      </path>
    </svg>
  );
};

export default AIBrainIcon;









